import {
  Box,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import { OutgoingInvoiceTypeEnum } from "@src/__generated__/graphql";
import { AbraFlexiExportedIcon } from "@src/components/modules/common/exports/abraflexi/AbraFlexiExportedIcon";
import { DocumentExportIndicator } from "@src/components/modules/common/exports/document-export-indicator";
import { XeroExportedIcon } from "@src/components/modules/common/exports/xero/Icon";
import { EmailLogs } from "@src/components/modules/invoices/EmailLogs";
import type { OutgoingInvoiceModel } from "@src/components/modules/invoices/outgoing/listing/models";
import { Link } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { observer } from "mobx-react-lite";
import { Fragment } from "react";

export type InvoiceNoColumnProps = {
  invoice: Pick<
    OutgoingInvoiceModel,
    | "id"
    | "invoice_no"
    | "has_proforma_payments"
    | "proformaInvoicesPivot"
    | "abraflexi_exported"
    | "xero_exported"
    | "documentExports"
    | "emailLogs"
    | "type"
  >;
};
export const InvoiceNoColumn = observer(function InvoiceNoColumn({
  invoice,
}: InvoiceNoColumnProps) {
  return (
    <Box>
      <HStack>
        <Link
          href={{
            pathname: "/invoices/outgoing-invoices/detail",
            query: { id: invoice.id },
          }}
        >
          {invoice.invoice_no}
        </Link>

        {invoice.has_proforma_payments && (
          <Popover trigger="hover" placement="top-start" colorScheme="grey">
            <PopoverTrigger>
              <Box>
                <Icon name="currency-dollar-circle" color="grey.600" />
              </Box>
            </PopoverTrigger>

            <PopoverContent color="whiteAlpha.900" bg="gray.700">
              <PopoverArrow bg="gray.700" />

              <PopoverBody>
                <Text>
                  <Trans>This invoice has proforma payment:</Trans>
                </Text>

                <HStack divider={<Fragment>,&nbsp;</Fragment>}>
                  {invoice.proformaInvoicesPivot.map((proforma) => (
                    <Link
                      color="teal.400"
                      key={proforma.id}
                      href={{
                        pathname: "/invoices/proforma-invoices/detail",
                        query: {
                          id: proforma.id,
                        },
                      }}
                    >
                      #{proforma.invoice_no}
                    </Link>
                  ))}
                </HStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}

        {invoice.abraflexi_exported && <AbraFlexiExportedIcon />}

        {invoice.xero_exported && <XeroExportedIcon />}

        <DocumentExportIndicator documentExports={invoice.documentExports} />

        <EmailLogs
          type="outgoingInvoice"
          logs={invoice.emailLogs.map((log) => ({
            ...log,
            sent_at: new Date(log.sent_at),
          }))}
        />
      </HStack>

      {invoice.type === OutgoingInvoiceTypeEnum.ProformaPayment && (
        <Text>
          <Trans>Payment receipt</Trans>
        </Text>
      )}
    </Box>
  );
});
